import { FileVersionViewReference } from 'src/file/types';

type ServiceSpecificationPath = {
  serviceSpecification: string;
  paths: string[];
};

export enum TechCrewPermission {
  // PLANNING
  VIEW_PLANNING = 'VIEW_PLANNING',
  // DOCUMENTATION
  VIEW_DOCUMENTATION = 'VIEW_DOCUMENTATION',
  EDIT_DOCUMENTATION = 'EDIT_DOCUMENTATION',
  CONTROL_DOCUMENTATION = 'CONTROL_DOCUMENTATION',
  VIEW_ATTENDANCE_NAMES_WORKTIME_HOURS = 'VIEW_ATTENDANCE_NAMES_WORKTIME_HOURS',
  VIEW_ATTENDANCE_NAMES_COMPANY_PEOPLE_COMMENT = 'VIEW_ATTENDANCE_NAMES_COMPANY_PEOPLE_COMMENT',
  VIEW_BOQ_POSITIONS_HOURS = 'VIEW_BOQ_POSITIONS_HOURS',
  VIEW_ATTENDANCE_PEOPLE_COUNT = 'VIEW_ATTENDANCE_PEOPLE_COUNT',
  VIEW_ATTENDANCE_TOTAL_HOURS = 'VIEW_ATTENDANCE_TOTAL_HOURS',
  EXPORT_DATA_DOCUMENTATION = 'EXPORT_DATA_DOCUMENTATION',
  // ANALYSIS
  VIEW_ANALYSIS = 'VIEW_ANALYSIS',
  EDIT_ANALYSIS = 'EDIT_ANALYSIS',
  VIEW_DOCUMENTS = 'VIEW_DOCUMENTS',
}

type SnakeCaseToCamelCase<S extends string> =
  S extends `${infer A}_${infer B}${infer Rest}`
    ? `${Lowercase<A>}${Capitalize<SnakeCaseToCamelCase<`${B}${Rest}`>>}`
    : Lowercase<S>;

type NormalizedTechCrewPermission = {
  [K in TechCrewPermission]: `project:techCrew.permission.${SnakeCaseToCamelCase<K>}`;
};

export const MappedTechCrewPermission: NormalizedTechCrewPermission = {
  VIEW_PLANNING: 'project:techCrew.permission.viewPlanning',
  VIEW_DOCUMENTATION: 'project:techCrew.permission.viewDocumentation',
  EDIT_DOCUMENTATION: 'project:techCrew.permission.editDocumentation',
  CONTROL_DOCUMENTATION: 'project:techCrew.permission.controlDocumentation',
  VIEW_ATTENDANCE_NAMES_WORKTIME_HOURS:
    'project:techCrew.permission.viewAttendanceNamesWorktimeHours',
  VIEW_ATTENDANCE_NAMES_COMPANY_PEOPLE_COMMENT:
    'project:techCrew.permission.viewAttendanceNamesCompanyPeopleComment',
  VIEW_BOQ_POSITIONS_HOURS: 'project:techCrew.permission.viewBoqPositionsHours',
  VIEW_ATTENDANCE_PEOPLE_COUNT:
    'project:techCrew.permission.viewAttendancePeopleCount',
  VIEW_ATTENDANCE_TOTAL_HOURS:
    'project:techCrew.permission.viewAttendanceTotalHours',
  EXPORT_DATA_DOCUMENTATION:
    'project:techCrew.permission.exportDataDocumentation',
  VIEW_DOCUMENTS: 'project:techCrew.permission.viewDocuments',
  VIEW_ANALYSIS: 'project:techCrew.permission.viewAnalysis',
  EDIT_ANALYSIS: 'project:techCrew.permission.editAnalysis',
} as const;

export type TechCrewMember = {
  userId: string;
  permissions: TechCrewPermission[];
};

export type WorkerEntry = {
  name: string;
};

export type TechCrew = {
  _id: string;
  name: string;
  description?: string;
  controlled: boolean;
  admins: string[];
  members: TechCrewMember[];
  defaultView?: FileVersionViewReference;
  sheetSets: string[];
  nameTemplate?: string;
  categoryParameter?: string;
  project: string;
  liableUser?: string;
  liableCompany?: string;
  serviceSpecificationPathsRegie?: ServiceSpecificationPath;
  serviceSpecificationPathsWorkday?: ServiceSpecificationPath;
  plannedHours?: number;
  tagBasedHours?: TagBasedHours[];
  lvBasedHours?: LvBasedHours[];
  customProgressValues?: number[];
  startDate?: string;
  endDate?: string;
  createdBy: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt?: string;
  color: string;
  workers?: WorkerEntry[];
};

export type TechCrewUsage = TechCrew & { isUsed: boolean };

export type LvBasedHours = {
  lvPath: string;
  plannedHours: number;
};

export type TagBasedHours = {
  tag: string;
  plannedHours: number;
};

export type CreateTechCrew = {
  name: string;
  description?: string;
  members?: TechCrewMember[];
  color: string;
  sheetSets: string[];
  defaultView?: FileVersionViewReference;
  nameTemplate?: string;
  plannedHours?: number;
  startDate?: string;
  endDate?: string;
  categoryParameter?: string;
};

export type UpdateTechCrew = {
  name?: string;
  color?: string;
  description?: string;
  admins?: string[];
  members?: TechCrewMember[];
  sheetSets?: string[];
  defaultView?: FileVersionViewReference;
  categoryParameter?: string;
  nameTemplate?: string;
  startDate?: string;
  endDate?: string;
  plannedHours?: number;
  tagBasedHours?: TagBasedHours[];
  lvBasedHours?: LvBasedHours[];
  workers?: WorkerEntry[];
};
